import * as v from 'valibot';

import { reportThrottled } from '#shared';

import { checkValue, reportSettingsDrift } from '../lib';

import type {
  EchoQueue,
  GlobalSettings,
  SettingsFrame,
  SettingsOrigin,
  SettingViolation,
  UserParameters,
  UserParametersFrame,
} from '../model';

const REPORT_THROTTLE_MS = 60_000;

const nullableString = v.nullable(v.string());

const globalSettingSchema = v.object({
  key: v.string(),
  value: v.unknown(),
  source: v.picklist(['configured', 'unset', 'code']),
  /** The built-in value; optional so a payload without it still parses. */
  default: v.optional(v.unknown()),
  updatedAt: nullableString,
  updatedBy: nullableString,
});

const userParameterSchema = v.object({
  key: v.string(),
  value: v.unknown(),
  default: v.unknown(),
  overridden: v.boolean(),
  updatedAt: nullableString,
});

const globalEntries = {
  settings: v.array(globalSettingSchema),
  ts: v.number(),
};
const userEntries = {
  parameters: v.array(userParameterSchema),
  ts: v.number(),
};

const globalEnvelopeSchema = v.object(globalEntries);
const userEnvelopeSchema = v.object(userEntries);
const globalFrameSchema = v.object({
  kind: v.literal('settings'),
  ...globalEntries,
});
const userFrameSchema = v.object({
  kind: v.literal('userParameters'),
  ...userEntries,
});

const violationSchema = v.object({
  path: v.string(),
  constraint: v.string(),
  bound: v.optional(v.union([v.number(), v.array(v.string())])),
});
const rejectionSchema = v.object({
  statusCode: v.literal(400),
  key: v.string(),
  violations: v.array(violationSchema),
});

const driftOf = (
  key: string,
  values: readonly unknown[],
  origin: SettingsOrigin,
): boolean => {
  const drifted = values.find(value => checkValue(key, value) === 'drift');
  if (drifted === undefined) {
    return false;
  }
  reportSettingsDrift({ direction: 'inbound', key, value: drifted, origin });
  return true;
};

const echoQueuesSchema = v.object({
  queues: v.array(
    v.object({
      url: v.string(),
      name: v.string(),
      enabled: v.boolean(),
      backlog: v.nullable(v.number()),
    }),
  ),
});

type GlobalEnvelope = v.InferOutput<typeof globalEnvelopeSchema>;
type UserEnvelope = v.InferOutput<typeof userEnvelopeSchema>;

const markGlobal = (
  envelope: GlobalEnvelope,
  origin: SettingsOrigin,
): GlobalSettings => ({
  ts: envelope.ts,
  settings: envelope.settings.map(row => ({
    ...row,
    drift: driftOf(row.key, row.source === 'unset' ? [] : [row.value], origin),
  })),
});

const markUser = (
  envelope: UserEnvelope,
  origin: SettingsOrigin,
): UserParameters => ({
  ts: envelope.ts,
  parameters: envelope.parameters.map(row => ({
    ...row,
    drift: driftOf(row.key, [row.value, row.default], origin),
  })),
});

const reportBadEnvelope = (
  site: 'global' | 'user' | 'echo queues',
  issues: readonly v.BaseIssue<unknown>[],
): never => {
  reportThrottled(
    `settings:bad-envelope-${site}`,
    `Malformed ${site} settings envelope`,
    {
      throttleMs: REPORT_THROTTLE_MS,
      extra: {
        issues: issues.map(issue => ({
          path: v.getDotPath(issue),
          message: issue.message,
        })),
      },
    },
  );
  throw new Error(`Malformed ${site} settings envelope`);
};

export const parseGlobalSettings = (
  raw: unknown,
  origin: SettingsOrigin,
): GlobalSettings => {
  const parsed = v.safeParse(globalEnvelopeSchema, raw);
  if (!parsed.success) {
    return reportBadEnvelope('global', parsed.issues);
  }
  return markGlobal(parsed.output, origin);
};

export const parseUserParameters = (
  raw: unknown,
  origin: SettingsOrigin,
): UserParameters => {
  const parsed = v.safeParse(userEnvelopeSchema, raw);
  if (!parsed.success) {
    return reportBadEnvelope('user', parsed.issues);
  }
  return markUser(parsed.output, origin);
};

export const parseEchoQueues = (raw: unknown): EchoQueue[] => {
  const parsed = v.safeParse(echoQueuesSchema, raw);
  if (!parsed.success) {
    return reportBadEnvelope('echo queues', parsed.issues);
  }
  return parsed.output.queues;
};

type OnInvalidFrame = (issuePaths: string[]) => void;

const issuePaths = (issues: readonly v.BaseIssue<unknown>[]): string[] =>
  issues.map(issue => v.getDotPath(issue) ?? '');

export const validateSettingsFrame = (
  p: unknown,
  onInvalid?: OnInvalidFrame,
): SettingsFrame | null => {
  const parsed = v.safeParse(globalFrameSchema, p);
  if (!parsed.success) {
    onInvalid?.(issuePaths(parsed.issues));
    return null;
  }
  return { kind: 'settings', ...markGlobal(parsed.output, 'frame') };
};

export const validateUserParametersFrame = (
  p: unknown,
  onInvalid?: OnInvalidFrame,
): UserParametersFrame | null => {
  const parsed = v.safeParse(userFrameSchema, p);
  if (!parsed.success) {
    onInvalid?.(issuePaths(parsed.issues));
    return null;
  }
  return { kind: 'userParameters', ...markUser(parsed.output, 'frame') };
};

const hasStatus = (
  error: unknown,
): error is { status: number; body?: unknown } =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  typeof error.status === 'number';

export const readSettingRejection = (
  error: unknown,
): SettingViolation[] | null => {
  if (!hasStatus(error) || error.status !== 400) {
    return null;
  }
  const parsed = v.safeParse(rejectionSchema, error.body);
  return parsed.success ? parsed.output.violations : null;
};
